import Phaser from "phaser";
import i18next from "i18next";
import { WindowManager } from "../../../ui/window-manager";
import { BaseEquipmentWindow } from "../windows/base-equipment-window";
import { BaseInventoryWindow } from "../windows/base-inventory-window";
import { StorageWindow } from "../windows/storage-window";
import { CosmeticsWindow } from "../../../ui/windows/cosmetics-window";
import { NameInputWindow } from "../windows/name-input-window";
import { EquipType, Item, SquadMemberDetail, SquadUpdateData, getArmorItemsFromEquipment, getHatSpriteKey } from "../../../models/common";
import { ArmorShieldBar } from "../../../ui/armor-shield-bar";
import { SquadPartnerDisplay } from "../../../ui/squad-partner-display";
import { ModernButton } from "../../../ui/modern-button";
import { NavButton } from "../../../ui/nav-button";
import { COLORS, hexToPhaser } from "../../../ui/colors";
import { getSafeAreaInsetLeft } from "../../../utils/mobile-utils";
import { NewsWindow } from "../windows/news-window";

const IDLE_ANIM_KEY = "idle";
// Hat bobs down this many pixels (1px scaled by the preview sprite's 3.2x scale) on the
// second idle frame, in sync with the 2-frame body animation, then back up.
const HAT_BOB_OFFSET = 1 * 3.2;

export class RaiderScreen {
  private readonly scene: Phaser.Scene;
  private readonly panel: Phaser.GameObjects.Container;
  private readonly windowManager: WindowManager;
  private readonly equipmentWindow: BaseEquipmentWindow;
  private readonly newsWindow: NewsWindow;
  private readonly storageWindow;
  private readonly cosmeticsWindow: CosmeticsWindow;
  private readonly pedestal: Phaser.GameObjects.Graphics;
  private readonly pedestalShadow: Phaser.GameObjects.Ellipse;
  private readonly playerSprite: Phaser.GameObjects.Sprite;
  private readonly weaponSprite: Phaser.GameObjects.Sprite;
  private readonly hatSprite: Phaser.GameObjects.Sprite;
  private readonly hatBaseY: number;
  private readonly armorBar: ArmorShieldBar;
  private readonly squadPartnerDisplay: SquadPartnerDisplay;
  private readonly changeSkinButton: ModernButton;
  private playerNameText: Phaser.GameObjects.Text | null = null;
  private playerNameBg: Phaser.GameObjects.Graphics | null = null;
  private playerNameEditBtn: Phaser.GameObjects.GameObject | null = null;
  private creditsText: Phaser.GameObjects.Text | null = null;
  private creditsIcon: Phaser.GameObjects.Sprite | null = null;
  private creditsBg: Phaser.GameObjects.Graphics | null = null;
  private marketButton: NavButton | null = null;
  private readonly nameInputWindow: NameInputWindow;
  private readonly uiLayer: Phaser.GameObjects.Container;
  private readonly playerX: number;
  private readonly playerExpandedX: number;
  private readonly playerY: number;

  private readonly baseX: number;
  private readonly baseY: number;
  private readonly expandedX: number;
  private readonly storageTargetX: number;
  private readonly storageHiddenX: number;

  private expanded: boolean = false;
  private selectedEquipType: EquipType | null = null;
  private skinsWindowOpen: boolean = false;
  private readonly onPlayerData: () => void;
  private readonly onStorageItems: () => void;

  constructor(
    scene: Phaser.Scene,
    windowManager: WindowManager,
    panel: Phaser.GameObjects.Container,
    uiLayer: Phaser.GameObjects.Container,
  ) {
    this.scene = scene;
    this.panel = panel;
    this.windowManager = windowManager;
    this.uiLayer = uiLayer;

    const { width, height } = this.scene.scale;
    this.playerX = Math.floor(width / 2);
    this.playerExpandedX = this.playerX - 46;
    this.playerY = Math.floor(height * 0.48);

    this.storageWindow = new StorageWindow(scene);
    this.equipmentWindow = new BaseEquipmentWindow(this.scene);
    this.newsWindow = new NewsWindow(this.scene);
    this.newsWindow.setUiLayer(this.uiLayer);
    this.equipmentWindow.setScreenContainer(this.panel);
    this.equipmentWindow.setOnSlotSelectionChange((selected, equipType) => {
      this.setExpanded(selected, equipType);
    });
    this.cosmeticsWindow = new CosmeticsWindow(scene, uiLayer);
    this.nameInputWindow = new NameInputWindow(scene, uiLayer);

    this.baseX = Math.floor(width * 3 / 4 - 118);
    this.baseY = -48;
    this.expandedX = this.baseX - 180;
    this.storageTargetX = this.baseX;
    this.storageHiddenX = this.scene.scale.width + 140;

    this.onPlayerData = () => {
      // Force full re-render of equipment window
      this.equipmentWindow.show();
      // Always set correct position after show
      const equipmentContainer = this.getEquipmentContainer();
      if (equipmentContainer) {
        if (this.expanded) {
          const { equipmentX } = this.getCenteredWindowPositions();
          equipmentContainer.setPosition(equipmentX, this.baseY);
        } else {
          equipmentContainer.setPosition(this.baseX, this.baseY);
        }
      }
      // Sync weapon sprite when equipment changes
      if (this.playerSprite.visible) {
        this.syncPlayerDisplay();
      }
      // Update player name in case it arrived after initial render
      if (this.playerNameText) {
        this.playerNameText.setText((window as any).player?.data?.name || "Guest");
      }
    };

    this.onStorageItems = () => {
      // Force full re-render of storage window
      this.storageWindow.show(true);
      // Always set correct position after show
      const storageContainer = this.getStorageContainer();
      if (storageContainer) {
        if (this.expanded) {
          const { storageX } = this.getCenteredWindowPositions();
          storageContainer.setPosition(storageX, this.baseY);
          this.storageWindow.setScrollMaskOffset(storageX, this.baseY);
        } else {
          storageContainer.setPosition(this.storageHiddenX, this.baseY);
        }
      }
    };

    this.registerSocketListeners();

    this.squadPartnerDisplay = new SquadPartnerDisplay(this.scene, this.panel, this.playerX, this.playerY, "raider");

    this.pedestalShadow = this.scene.add.ellipse(
      this.playerX,
      this.playerY + 24,
      178,
      38,
      0x000000,
      0.35,
    );

    this.pedestal = this.scene.add.graphics();
    this.pedestal.fillStyle(0x141a28, 1);
    this.pedestal.fillEllipse(this.playerX, this.playerY + 10, 164, 36);
    this.pedestal.fillStyle(0x2f4f88, 1);
    this.pedestal.fillEllipse(this.playerX, this.playerY + 4, 150, 26);
    this.pedestal.lineStyle(3, 0x8cb7ef, 1);
    this.pedestal.strokeEllipse(this.playerX, this.playerY + 4, 150, 26);

    this.playerSprite = this.scene.add.sprite(
      this.playerX,
      this.playerY - 42,
      "player",
      0,
    );
    this.playerSprite.setScale(3.2);

    this.weaponSprite = this.scene.add.sprite(
      this.playerX,
      this.playerY - 22,
      "weapon",
      0,
    );
    this.weaponSprite.setOrigin(0, 0.5);
    this.weaponSprite.setScale(2.4);
    this.weaponSprite.setVisible(false);

    // Player sprite is centered at playerY - 42 with a 32px frame at scale 3.2, so its top
    // edge (the head) sits at playerY - 42 - 16 * 3.2 = playerY - 93.2. Nudged down 32px so
    // the hat rests on the head instead of floating above it.
    this.hatBaseY = this.playerY - 93.2 + 42;
    this.hatSprite = this.scene.add.sprite(
      this.playerX,
      this.hatBaseY,
      "hat-1",
      0,
    );
    this.hatSprite.setOrigin(0.5, 1);
    this.hatSprite.setScale(3.2);
    this.hatSprite.setVisible(false);

    // Bob the hat with the body's 2-frame idle animation
    this.playerSprite.on(
      Phaser.Animations.Events.ANIMATION_UPDATE,
      (
        _animation: Phaser.Animations.Animation,
        frame: Phaser.Animations.AnimationFrame,
      ) => {
        this.hatSprite.y = this.hatBaseY + (frame.index === 2 ? HAT_BOB_OFFSET : 0);
      },
    );

    this.armorBar = new ArmorShieldBar(
      this.scene,
      this.playerX - 40,
      this.playerY - 113,
      true,
      2,
      true,
    );
    this.panel.add(this.armorBar.bg);

    this.changeSkinButton = new ModernButton({
      scene: this.scene,
      x: this.playerX,
      y: this.playerY + 80,
      text: i18next.t("ui:buttons.changeLook"),
      style: "success",
      size: "small",
      onClick: () => {
        this.scene.events.emit("raider-open-cosmetics");
      },
    });

    panel.add([
      this.pedestalShadow,
      this.pedestal,
      ...this.squadPartnerDisplay.getDisplayObjects(),
      this.playerSprite,
      this.weaponSprite,
      this.hatSprite,
      this.changeSkinButton,
    ]);

    this.pedestalShadow.setVisible(false);
    this.pedestal.setVisible(false);
    this.playerSprite.setVisible(false);
    this.changeSkinButton.setVisible(false);
  }

  private getEquipmentContainer(): Phaser.GameObjects.Container | null {
    return (this.equipmentWindow as any).container as Phaser.GameObjects.Container | null;
  }

  private registerSocketListeners(): void {
    if (!window.socket) return;
    // Ensure single registration per RaiderScreen instance.
    window.socket.off("player-data", this.onPlayerData);
    window.socket.off("storage-items", this.onStorageItems);
    window.socket.on("player-data", this.onPlayerData);
    window.socket.on("storage-items", this.onStorageItems);
  }

  private unregisterSocketListeners(): void {
    if (!window.socket) return;
    window.socket.off("player-data", this.onPlayerData);
    window.socket.off("storage-items", this.onStorageItems);
  }

  private getStorageContainer(): Phaser.GameObjects.Container | null {
    return (this.storageWindow as any).container as Phaser.GameObjects.Container | null;
  }

  private tweenContainer(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    duration: number,
  ): void {
    this.scene.tweens.killTweensOf(container);
    this.scene.tweens.add({
      targets: container,
      x,
      y,
      duration,
      ease: "Cubic.Out",
    });
  }

  private hidePlayerDisplay(): void {
    this.pedestalShadow.setVisible(false);
    this.pedestal.setVisible(false);
    this.playerSprite.setVisible(false);
    this.weaponSprite.setVisible(false);
    this.hatSprite.setVisible(false);
    this.changeSkinButton.setVisible(false);
    this.armorBar.hide();
    this.removePlayerNameDisplay();
    this.removeCreditsDisplay();
    this.squadPartnerDisplay.setScreenVisible(false);
  }

  public showCosmeticsWindow(onClose: () => void): void {
    this.cosmeticsWindow.setOnClose(onClose);
    this.cosmeticsWindow.show();
  }

  public restoreEquipmentDisplay(): void {
    this.skinsWindowOpen = false;
    this.pedestalShadow.setVisible(true);
    this.pedestal.setVisible(true);
    this.playerSprite.setVisible(true);
    this.changeSkinButton.setVisible(true);
    this.syncPlayerDisplay();
    this.createPlayerNameDisplay();
    this.createCreditsDisplay();
    this.squadPartnerDisplay.setScreenVisible(true);
    this.equipmentWindow.show();

    // Restore equipment window to correct position
    const equipmentContainer = this.getEquipmentContainer();
    if (equipmentContainer) {
      equipmentContainer.setPosition(this.baseX, this.baseY);
    }
  }

  private showPlayerDisplay(): void {
    if (this.skinsWindowOpen) return;
    this.pedestalShadow.setVisible(true);
    this.pedestal.setVisible(true);
    this.playerSprite.setVisible(true);
    this.changeSkinButton.setVisible(true);
    this.syncPlayerDisplay();
    this.createPlayerNameDisplay();
    this.createCreditsDisplay();
    this.squadPartnerDisplay.setScreenVisible(true);
  }

  private getCenteredWindowPositions(): { equipmentX: number; storageX: number } {
    const gameWidth = this.scene.scale.width;
    const screenCenter = gameWidth / 2;
    const equipmentWidth = 140;
    const storageWidth = 372; // Actual rendered width of storage window
    const gap = 20;

    const totalWidth = equipmentWidth + gap + storageWidth;
    const leftEdge = screenCenter - totalWidth / 2 - 100;

    const equipmentX = leftEdge + equipmentWidth / 2;
    const storageX = leftEdge + equipmentWidth + gap + storageWidth / 2 - 80;

    return { equipmentX, storageX };
  }

  private setExpanded(nextExpanded: boolean, equipType?: EquipType): void {
    if (this.skinsWindowOpen) return;
    
    if (this.expanded === nextExpanded) {
      if (this.expanded && equipType && equipType !== this.selectedEquipType) {
        this.selectedEquipType = equipType;
        (this.storageWindow as any).updateFilteredEquipType(equipType);
      }
      return;
    }
    this.expanded = nextExpanded;
    this.selectedEquipType = equipType || null;

    const equipmentContainer = this.getEquipmentContainer();
    const { equipmentX, storageX } = this.getCenteredWindowPositions();

    if (equipmentContainer) {
      this.tweenContainer(
        equipmentContainer,
        this.expanded ? equipmentX : this.baseX,
        this.baseY,
        220,
      );
    }

    if (this.expanded) {
      this.hidePlayerDisplay();
      this.storageWindow.setShowItemTypeButtons(false);
      this.storageWindow.setShowSellButton(false);
      this.storageWindow.setShowEquipButton(true);
      this.storageWindow.setShowBackButton(true);
      this.storageWindow.setOnBack(() => this.setExpanded(false));
      this.storageWindow.setScreenContainer(this.panel);
      if (this.selectedEquipType) {
        (this.storageWindow as any).setFilteredEquipType(this.selectedEquipType);
      }
      this.storageWindow.show(true);
      this.storageWindow.setScrollMaskOffset(storageX, this.baseY);

      const storageContainer = this.getStorageContainer();
      if (storageContainer) {
        storageContainer.setPosition(this.storageHiddenX, this.baseY);
        this.tweenContainer(storageContainer, storageX, this.baseY, 240);
      }
    } else {
      this.showPlayerDisplay();
      this.storageWindow.setShowEquipButton(false);
      this.storageWindow.setShowBackButton(false);
      const storageContainer = this.getStorageContainer();
      if (storageContainer) {
        this.scene.tweens.killTweensOf(storageContainer);
        this.scene.tweens.add({
          targets: storageContainer,
          x: this.storageHiddenX,
          y: this.baseY,
          duration: 200,
          ease: "Cubic.In",
          onComplete: () => {
            this.storageWindow.hide();
          },
        });
      } else {
        this.storageWindow.hide();
      }

      this.setExpandedLayout(this.expanded);
    }
  }

  private redrawPedestalAt(x: number): void {
    this.pedestal.clear();
    this.pedestal.fillStyle(0x141a28, 1);
    this.pedestal.fillEllipse(x, this.playerY + 10, 164, 36);
    this.pedestal.fillStyle(0x2f4f88, 1);
    this.pedestal.fillEllipse(x, this.playerY + 4, 150, 26);
    this.pedestal.lineStyle(3, 0x8cb7ef, 1);
    this.pedestal.strokeEllipse(x, this.playerY + 4, 150, 26);
  }

  private setExpandedLayout(expanded: boolean): void {
    const targetX = expanded ? this.playerExpandedX : this.playerX;

    this.scene.tweens.killTweensOf(this.playerSprite);
    this.scene.tweens.killTweensOf(this.weaponSprite);
    this.scene.tweens.killTweensOf(this.pedestalShadow);

    this.scene.tweens.add({
      targets: this.playerSprite,
      x: targetX,
      duration: 220,
      ease: "Cubic.Out",
    });
    this.scene.tweens.add({
      targets: this.weaponSprite,
      x: targetX,
      duration: 220,
      ease: "Cubic.Out",
    });
    this.scene.tweens.add({
      targets: this.pedestalShadow,
      x: targetX,
      duration: 220,
      ease: "Cubic.Out",
    });

    const startX = this.pedestalShadow.x;
    const progressState = { t: 0 };
    this.scene.tweens.add({
      targets: progressState,
      t: 1,
      duration: 220,
      ease: "Cubic.Out",
      onUpdate: () => {
        const x = Phaser.Math.Linear(startX, targetX, progressState.t);
        this.redrawPedestalAt(x);
      },
      onComplete: () => {
        this.redrawPedestalAt(targetX);
      },
    });
  }

  private ensureIdleAnimation(textureKey: string): void {
    if (this.scene.anims.exists(IDLE_ANIM_KEY)) {
      this.scene.anims.remove(IDLE_ANIM_KEY);
    }

    this.scene.anims.create({
      key: IDLE_ANIM_KEY,
      frames: this.scene.anims.generateFrameNumbers(textureKey, {
        start: 0,
        end: 1,
      }),
      frameRate: 4,
      repeat: -1,
    });
  }

  private syncArmorBar(): void {
    const equipment = (window as any).player?.data?.equipment;
    if (!equipment) { this.armorBar.hide(); return; }
    const items = getArmorItemsFromEquipment(equipment);
    if (items.length > 0) {
      this.armorBar.setArmorItems(items);
      this.armorBar.show();
    } else {
      this.armorBar.hide();
    }
  }

  private syncPlayerDisplay(): void {
    const activeSkin = (window as any).player?.data?.activeSkin as string | undefined;
    const desiredTexture = activeSkin || "player";
    const textureKey = this.scene.textures.exists(desiredTexture)
      ? desiredTexture
      : "player";

    this.playerSprite.setTexture(textureKey, 0);
    this.playerSprite.setFlipX(false);
    this.ensureIdleAnimation(textureKey);
    this.playerSprite.play(IDLE_ANIM_KEY, true);

    const weapon = (window as any).player?.data?.equipment?.Weapon as Item | undefined;
    if (weapon && weapon.definition?.spriteSheet === "weapon") {
      const frame = weapon.definition.spriteY * 10 + weapon.definition.spriteX;
      this.weaponSprite.setFrame(frame);
      this.weaponSprite.setVisible(true);
    } else {
      this.weaponSprite.setVisible(false);
    }

    const equippedHat = (window as any).player?.data?.equippedHat as number | undefined;
    const hatTextureKey = getHatSpriteKey(equippedHat);
    if (hatTextureKey && this.scene.textures.exists(hatTextureKey)) {
      this.hatSprite.setTexture(hatTextureKey);
      this.hatSprite.setVisible(true);
    } else {
      this.hatSprite.setVisible(false);
    }

    this.syncArmorBar();
  }

  show(): void {
    if (this.skinsWindowOpen) return;

    // Re-register in case another scene performed broad socket cleanup.
    this.registerSocketListeners();

    this.windowManager.hideAll();

    this.equipmentWindow.setScreenContainer(this.panel);
    this.equipmentWindow.show();
    this.storageWindow.setScreenContainer(this.panel);
    this.storageWindow.hide();
    this.newsWindow.show();

    const equipmentContainer = this.getEquipmentContainer();
    if (equipmentContainer) {
      equipmentContainer.setPosition(this.baseX, this.baseY);
    }

    this.expanded = false;
    this.syncPlayerDisplay();
    this.setExpandedLayout(false);
    this.pedestalShadow.setVisible(true);
    this.pedestal.setVisible(true);
    this.playerSprite.setVisible(true);
    this.changeSkinButton.setVisible(true);
    this.squadPartnerDisplay.setScreenVisible(true);

    // Create and display player name
    this.createPlayerNameDisplay();
    // Create and display credits
    this.createCreditsDisplay();

    // Weapon UI in BaseScene is only intended for the Raider screen.
    this.windowManager.showWeaponUI();
  }

  hide(): void {
    this.equipmentWindow.hide();
    this.newsWindow.hide();
    this.storageWindow.hide();
    this.expanded = false;
    this.playerSprite.anims.stop();
    this.playerSprite.setFrame(0);
    this.hatSprite.y = this.hatBaseY;
    this.pedestalShadow.setVisible(false);
    this.pedestal.setVisible(false);
    this.playerSprite.setVisible(false);
    this.weaponSprite.setVisible(false);
    this.hatSprite.setVisible(false);
    this.changeSkinButton.setVisible(false);
    this.armorBar.hide();
    this.removePlayerNameDisplay();
    this.removeCreditsDisplay();
    this.squadPartnerDisplay.setScreenVisible(false);
    this.windowManager.hideWeaponUI();
  }

  private createPlayerNameDisplay(): void {
    if (this.skinsWindowOpen) return;
    this.removePlayerNameDisplay();

    const player = (window as any).player;
    const playerName = player?.data?.name || "Guest";

    // Player name text above sprite (moved up)
    this.playerNameText = this.scene.add.text(
      this.playerX,
      this.playerY - 140,
      playerName,
      {
        fontSize: "20px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
        align: "center",
      }
    );
    this.playerNameText.setOrigin(0.5, 0.5);
    this.playerNameText.setScrollFactor(0);
    this.playerNameText.setShadow(2, 2, "#000000", 0.7);

    const nameBgPadX = 14;
    const nameBgPadY = 8;
    this.playerNameBg = this.scene.add.graphics();
    this.playerNameBg.fillStyle(0x000000, 0.55);
    this.playerNameBg.fillRoundedRect(
      this.playerX - this.playerNameText.width / 2 - nameBgPadX,
      this.playerY - 140 - this.playerNameText.height / 2 - nameBgPadY,
      this.playerNameText.width + nameBgPadX * 2,
      this.playerNameText.height + nameBgPadY * 2,
      10,
    );
    this.playerNameBg.setScrollFactor(0);

    // Edit name button as ModernButton (small variant)
    const editNameButton = new ModernButton({
      scene: this.scene,
      x: this.playerX,
      y: this.playerY + 42,
      text: "Edit name",
      style: "secondary",
      size: "small",
      onClick: () => {
        const accountMail: string = (window as any).player?.data?.accountMail || "";
        if (!accountMail.trim()) {
          // Let BaseScene route unauthenticated users into account creation flow.
          this.scene.events.emit("raider-edit-name-requires-account");
          return;
        }

        this.nameInputWindow.show(playerName, () => {
          // Callback when name input closes - refresh the display
          this.createPlayerNameDisplay();
        });
      },
    });
    this.playerNameEditBtn = editNameButton;

    this.panel.add([this.playerNameBg, this.playerNameText, editNameButton]);
  }

  private removePlayerNameDisplay(): void {
    if (this.playerNameBg) {
      this.panel.remove(this.playerNameBg, true);
      this.playerNameBg = null;
    }
    if (this.playerNameText) {
      this.panel.remove(this.playerNameText, true);
      this.playerNameText = null;
    }
    if (this.playerNameEditBtn) {
      this.panel.remove(this.playerNameEditBtn, true);
      this.playerNameEditBtn = null;
    }
  }

  private createCreditsDisplay(): void {
    if (this.skinsWindowOpen) return;
    this.removeCreditsDisplay();

    const player = (window as any).player;
    const credits = player?.data?.credits || 0;

    const { height } = this.scene.scale;

    // Mirror createSideButtons: shift right by the safe-area inset so the group
    // stays visually aligned with the side-button column on all devices.
    const safeLeft = getSafeAreaInsetLeft();
    // sideButtonCenterX = marginX(16) + safeLeft + btnSize/2(24)
    const sideButtonCenterX = 40 + safeLeft;
    const groupCenterX = sideButtonCenterX + 140;

    const creditsY = height / 2 + 40;

    // Market button centered at groupCenterX
    this.marketButton = new NavButton({
      scene: this.scene,
      x: groupCenterX,
      y: creditsY - 70,
      text: "MARKET",
      icon: "shop-icon",
      style: "orange",
      width: 68,
      onClick: () => this.scene.events.emit("raider-market-clicked"),
    });
    this.panel.add(this.marketButton);

    // Build text first so we can measure its width for precise centering
    this.creditsText = this.scene.add.text(
      0,
      creditsY,
      `${credits} Credits`,
      {
        fontSize: "18px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
        align: "left",
        shadow: {
          offsetX: 0,
          offsetY: 3,
          color: "#000000",
          blur: 0,
          stroke: true,
          fill: true,
        },
      }
    );
    this.creditsText.setOrigin(0, 0.5);
    this.creditsText.setScrollFactor(0);

    // Center the icon+text row at groupCenterX so it aligns with the market button
    const iconW = Math.round(16 * 1.2); // 16px sprite frame * 1.2 scale ≈ 19px
    const iconTextGap = 6;
    const rowWidth = iconW + iconTextGap + this.creditsText.width;
    const rowLeft = groupCenterX - rowWidth / 2;

    // Credits icon (items spritesheet, frame 202)
    this.creditsIcon = this.scene.add.sprite(rowLeft + iconW / 2, creditsY, "items", 202);
    this.creditsIcon.setScale(1.2);
    this.creditsIcon.setScrollFactor(0);

    this.creditsText.setX(rowLeft + iconW + iconTextGap);

    const creditsBgPadX = 12;
    const creditsBgPadY = 8;
    const creditsBgHeight = Math.max(iconW, this.creditsText.height) + creditsBgPadY * 2;
    this.creditsBg = this.scene.add.graphics();
    this.creditsBg.fillStyle(0x000000, 0.55);
    this.creditsBg.fillRoundedRect(
      rowLeft - creditsBgPadX,
      creditsY - creditsBgHeight / 2,
      rowWidth + creditsBgPadX * 2,
      creditsBgHeight,
      10,
    );
    this.creditsBg.setScrollFactor(0);

    this.panel.add([this.creditsBg, this.creditsIcon, this.creditsText]);
  }

  private removeCreditsDisplay(): void {
    if (this.marketButton) {
      this.panel.remove(this.marketButton, true);
      this.marketButton = null;
    }
    if (this.creditsText) {
      this.panel.remove(this.creditsText, true);
      this.creditsText = null;
    }
    if (this.creditsIcon) {
      this.panel.remove(this.creditsIcon, true);
      this.creditsIcon = null;
    }
    if (this.creditsBg) {
      this.panel.remove(this.creditsBg, true);
      this.creditsBg = null;
    }
  }

  // ── Squad presentation ────────────────────────────────────────────────────

  public updateSquadState(memberDetails: SquadMemberDetail[], myAccountId: number | undefined): void {
    this.squadPartnerDisplay.updateState(memberDetails, myAccountId);
  }

  public updateSquadMemberData(data: SquadUpdateData): void {
    this.squadPartnerDisplay.updateMemberData(data);
  }

  public clearSquadState(): void {
    this.squadPartnerDisplay.clear();
  }

  public destroy(): void {
    this.unregisterSocketListeners();
  }
}